<template>
  <label
    v-if="isNativeControl"
    :class="[nativeRootClasses, inheritedClass]"
    :style="inheritedStyle"
    :data-testid="sectionTag"
  >
    <input
      v-bind="passthroughAttrs"
      class="w-checkbox__native-input"
      type="checkbox"
      :checked="isChecked"
      :disabled="disabled"
      :name="name"
      :value="value"
      :aria-checked="indeterminate ? 'mixed' : isChecked"
      @change="handleNativeChange"
    />
    <span class="w-checkbox__box" aria-hidden="true" />
    <span v-if="label || slots.label" class="w-checkbox__label label-regular-medium">
      <slot name="label">{{ label }}</slot>
    </span>
  </label>

  <v-checkbox
    v-else
    v-model="model"
    :class="[vuetifyRootClasses, inheritedClass]"
    :style="inheritedStyle"
    :data-testid="sectionTag"
    :disabled="disabled"
    :indeterminate="indeterminate"
    :label="label"
    :name="name"
    :true-value="trueValue"
    :false-value="falseValue"
    :value="value"
    color="primary"
    hide-details
    density="compact"
    v-bind="passthroughAttrs"
  >
    <template v-if="slots.label" #label>
      <slot name="label"></slot>
    </template>
  </v-checkbox>
</template>

<script setup lang="ts">
import { computed, nextTick, useAttrs, useSlots } from 'vue'
import { tokens } from '@/plugins/vuetify'
import type {
  WSelectionControlModelValue,
  WSelectionControlValue,
} from './WSelectionControl.types'

defineOptions({
  inheritAttrs: false,
})

export interface WCheckboxProps {
  sectionTag?: string
  indeterminate?: boolean
  size?: 'default' | 'large' | 'small' | string
  variant?: 'default' | 'no-padding' | string
  label?: string
  disabled?: boolean
  name?: string
  value?: WSelectionControlValue
  trueValue?: WSelectionControlValue
  falseValue?: WSelectionControlValue
}

const props = withDefaults(defineProps<WCheckboxProps>(), {
  sectionTag: 'unspecified',
  indeterminate: false,
  size: 'default',
  variant: 'default',
  label: undefined,
  disabled: false,
  name: undefined,
  value: undefined,
  trueValue: undefined,
  falseValue: undefined,
})

const model = defineModel<WSelectionControlModelValue>({ default: false })
const attrs = useAttrs()
const slots = useSlots()

const isNativeControl = computed(() => props.size === 'small' && props.variant === 'no-padding')

const inheritedClass = computed(() => attrs.class)
const inheritedStyle = computed(() => attrs.style)
const passthroughAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

const isChecked = computed(() => {
  if (Array.isArray(model.value)) {
    return props.value !== undefined && model.value.includes(props.value)
  }

  if (props.trueValue !== undefined || props.falseValue !== undefined) {
    return model.value === (props.trueValue ?? true)
  }

  if (props.value !== undefined) {
    return model.value === props.value
  }

  return Boolean(model.value)
})

const vuetifyRootClasses = computed(() => ({
  'w-checkbox--indeterminate': props.indeterminate,
  'w-checkbox--large': props.size === 'large',
}))

const nativeRootClasses = computed(() => ({
  'w-checkbox': true,
  'w-checkbox--native': true,
  'w-checkbox--small-no-padding': isNativeControl.value,
  'w-checkbox--checked': isChecked.value,
  'w-checkbox--indeterminate': props.indeterminate,
  'w-checkbox--disabled': props.disabled,
}))

function handleNativeChange(event: Event) {
  if (props.disabled) {
    return
  }

  const target = event.target as HTMLInputElement

  if (Array.isArray(model.value)) {
    if (props.value === undefined) {
      return
    }

    model.value = target.checked
      ? [...model.value, props.value]
      : model.value.filter((currentValue) => currentValue !== props.value)
  } else if (props.trueValue !== undefined || props.falseValue !== undefined) {
    model.value = target.checked ? (props.trueValue ?? true) : (props.falseValue ?? false)
  } else if (props.value !== undefined) {
    model.value = target.checked ? props.value : null
  } else {
    model.value = target.checked
  }

  // Native checkboxes mutate before change fires. Restore the DOM from the model after
  // synchronous parent updates settle so a rejected controlled update cannot stay visible.
  void nextTick(() => {
    target.checked = isChecked.value
  })
}
</script>

<style scoped lang="scss">
.w-checkbox--native {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  width: fit-content;
  color: v-bind('tokens.color.content.text.secondary');
  cursor: pointer;
}

.w-checkbox--small-no-padding {
  min-width: 16px;
  min-height: 16px;
  line-height: 0;
  vertical-align: middle;
}

.w-checkbox--disabled {
  cursor: default;
}

.w-checkbox__native-input {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 16px;
  height: 16px;
  margin: 0;
  opacity: 0;
  cursor: inherit;
}

.w-checkbox__box {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  border: 2px solid v-bind('tokens.color.control.border.default');
  border-radius: v-bind('tokens.borderRadius.control.small');
  background: transparent;
  overflow: hidden;
}

.w-checkbox--checked .w-checkbox__box {
  border-color: v-bind('tokens.color.control.border.selected');
  background: v-bind('tokens.color.control.border.selected');
}

.w-checkbox--indeterminate .w-checkbox__box {
  border-color: v-bind('tokens.color.control.border.default');
  background: v-bind('tokens.color.control.border.default');
}

.w-checkbox--disabled .w-checkbox__box {
  border-color: v-bind('tokens.color.control.border.disabled');
}

.w-checkbox--disabled.w-checkbox--checked .w-checkbox__box,
.w-checkbox--disabled.w-checkbox--indeterminate .w-checkbox__box {
  border-color: v-bind('tokens.color.control.border.disabled');
  background: v-bind('tokens.color.control.border.disabled');
}

.w-checkbox--checked .w-checkbox__box::after {
  display: block;
  width: 9px;
  height: 5px;
  margin-top: -2px;
  border-bottom: 2px solid v-bind('tokens.color.control.icon.primary');
  border-left: 2px solid v-bind('tokens.color.control.icon.primary');
  content: '';
  transform: rotate(-45deg);
}

.w-checkbox--indeterminate .w-checkbox__box::after {
  display: block;
  width: 10px;
  height: 3px;
  background: v-bind('tokens.color.control.icon.primary');
  content: '';
}

.w-checkbox--native .w-checkbox__native-input:focus-visible + .w-checkbox__box {
  outline: 2px solid v-bind('tokens.color.focus.ring');
  outline-offset: 2px;
}

.w-checkbox__label {
  color: v-bind('tokens.color.content.text.secondary');
}

.w-checkbox--disabled .w-checkbox__label {
  color: v-bind('tokens.color.content.text.muted');
}

:deep(.v-checkbox-btn > .v-label) {
  margin-left: 12px;
  color: v-bind('tokens.color.content.text.secondary');
  opacity: 1;
}

:deep(
    .v-checkbox-btn:not(.v-selection-control--dirty)
      > .v-selection-control__wrapper
      > .v-selection-control__input
      > .v-icon
  ) {
  color: v-bind('tokens.color.control.border.default');
  opacity: 1;
}

:deep(.v-selection-control--disabled) {
  opacity: 1;
}

:deep(
    .v-selection-control--disabled:not(.v-selection-control--dirty)
      > .v-selection-control__wrapper
      > .v-selection-control__input
      > .v-icon
  ) {
  color: v-bind('tokens.color.control.border.disabled');
  opacity: 1;
}

:deep(
    .v-selection-control--disabled.v-selection-control--dirty
      > .v-selection-control__wrapper
      > .v-selection-control__input
      > .v-icon
  ) {
  color: v-bind('tokens.color.control.border.disabled') !important;
  opacity: 1;
}

:deep(.v-checkbox-btn.v-selection-control--disabled > .v-label) {
  color: v-bind('tokens.color.content.text.muted');
  opacity: 1;
}

:deep(.v-selection-control__input::before) {
  border-radius: v-bind('tokens.borderRadius.control.full');
}

:deep(
    .v-checkbox-btn:not(.v-selection-control--dirty):not(.v-selection-control--disabled):hover
      > .v-selection-control__wrapper
      > .v-selection-control__input::before
  ) {
  background-color: v-bind('tokens.color.control.background.hover');
  opacity: 1;
}

:deep(
    .v-checkbox-btn.v-selection-control--dirty:not(.v-selection-control--disabled):hover
      > .v-selection-control__wrapper
      > .v-selection-control__input::before
  ) {
  background-color: v-bind('tokens.color.control.background.selected');
  opacity: 1;
}

:deep(
    .v-checkbox-btn.v-selection-control--disabled
      > .v-selection-control__wrapper
      > .v-selection-control__input::before
  ) {
  display: none;
}

.w-checkbox--indeterminate :deep(
    .v-checkbox-btn.v-selection-control--dirty:not(.v-selection-control--disabled)
      > .v-selection-control__wrapper
      > .v-selection-control__input
      > .v-icon
  ) {
  color: v-bind('tokens.color.control.border.default') !important;
}

.w-checkbox--indeterminate :deep(
    .v-checkbox-btn.v-selection-control--dirty:not(.v-selection-control--disabled):hover
      > .v-selection-control__wrapper
      > .v-selection-control__input::before
  ) {
  background-color: v-bind('tokens.color.control.background.hover');
}

.w-checkbox--large :deep(.v-selection-control__wrapper) {
  height: 42px;
  width: 42px;
}

.w-checkbox--large :deep(.v-selection-control__input) {
  height: 42px;
  width: 42px;
}

.w-checkbox--large :deep(.v-selection-control__input > .v-icon) {
  font-size: 28px;
}
</style>
